import { OutlineElement } from '../../base/outline-element/outline-element';
import { CSSResultGroup, html, TemplateResult } from 'lit';
import { customElement } from 'lit/decorators.js';

import componentStyles from './ghs-side-navigation.css.lit';
import '../ghs-icon/ghs-icon';

import { SlotController } from '../../controllers/slot-controller';

/**
 * The Subnav Component
 *
 * @element ghs-navigation
 * @slot accepts nested ul li elements to create the subnav component
 */
@customElement('ghs-side-navigation')
export class GhsSideNavigation extends OutlineElement {
  static styles: CSSResultGroup = [componentStyles];

  slots = new SlotController(this, true);

  topLevels: NodeListOf<Element>;

  navPaddingRight = 48;
  translateX = 0;

  nextItemPosition = 0;
  prevItemPosition = 0;

  lastItemPosition: number;

  createIcon(name: string): HTMLElement {
    const icon = document.createElement('ghs-icon');

    icon.setAttribute('name', name);
    icon.setAttribute('size', '20px');
    icon.setAttribute('site', this.site);

    return icon;
  }

  firstUpdated(): void {
    super.firstUpdated();
    // This timeout is important for slot controller to finish before these things run.
    setTimeout(() => {
      // @ts-expect-error shadowRoot makes this think it is not an element because reasons.
      this.topLevels = this.shadowRoot.querySelectorAll('.dropdown-trigger');
      this.addListeners();
    }, 0);
    /**
     * We have an intersection of the slot controller, a very complex drupal
     * rendering template, and goodness knows what else which makes this too
     * buggy to deploy right before pencils down. I didn't want to delete it
     * though because it will likely become a feature-ask in the next SOW.
     * This little block of code looks for the "is-active" class Drupal puts on
     * the active item in a menu. Trouble is, sometimes it appears on <li>
     * elements, sometimes on <a> elements, and sometimes on both. Because of
     * the slot controller, I think anyway, the "is-active" classes don't
     * always correctly get copied into the shadow DOM, so the slot content
     * is mostly correct, but what gets pulled in is not. Ideally we would:
     * 1) Update the template so that <li> elements consistently get the active
     *    class.
     * 2) Refactor this component to use the light controller and not
     *    shadow-shift its content. This would also likely eliminate the need
     *    for the timeout, as it no longer has to wait for the slot controller
     *    to finish.
     * 3) Move this little blob of code to its own function/s with a recursive
     *    call to climb up the DOM until it hits its parent <nav> element
     *    setting active states as it goes.
     *
     * setTimeout(() => {
     * this.shadowRoot?.querySelectorAll('.is-active').forEach(el => {
     *   if (el?.classList.contains('is-active')) {
     *     this.activeDropdownEl(el);
     *     () => {if(el.parentElement && el.parentElement.tagName != 'nav') {
     *       this.activeDropdownEl(el.parentElement);
     *     }};
     *   }
     * });}, 100 )
     */
  }

  /**
   * Active dropdown using the focused ID.
   */
  activeDropdownEl(el: Element | null): void {
    if (el) {
      const elItem = el.closest('li');
      const siblings = elItem?.closest('ul')?.querySelectorAll('li');

      siblings?.forEach(item => {
        this.inactiveDropdownEl(item);
      });

      if (elItem) {
        elItem.setAttribute('is-dropdown-open', 'true');
        el.setAttribute('aria-expanded', 'true');
      }
    }
  }

  /**
   * Inactive dropdown using the focused ID.
   */
  inactiveDropdownEl(el: Element | null): void {
    if (el) {
      const parent = el.closest('li');

      if (parent) {
        parent.removeAttribute('is-dropdown-open');
        el?.setAttribute('aria-expanded', 'false');
      }
    }
  }

  /**
   * Inactive dropdown using the focused ID.
   */
  handleClickDropdownEl(el: Element | null): void {
    if (el) {
      const selector = el.closest('li');

      if (selector) {
        const isOpen = selector.getAttribute('is-dropdown-open');
        isOpen ? this.inactiveDropdownEl(el) : this.activeDropdownEl(el);
      }
    }
  }

  /**
   * Listens for mouse and keyboard events.
   */
  addListeners(): void {
    const mainItems = [...this.topLevels.values()];

    mainItems.map(el => {
      const keyboardTrigger = el.querySelector('.dropdown-trigger');

      el.addEventListener('click', () => this.handleClickDropdownEl(el));

      /* eslint-disable  @typescript-eslint/no-explicit-any */
      keyboardTrigger?.addEventListener('keydown', (ev: any) => {
        const { code } = ev;

        if (code === 'Enter' || code === 'Space') {
          this.activeDropdownEl(el);
        }
      });
    });
  }

  render(): TemplateResult {
    return html`
      <div class="navigation">
        <slot name="navigation"></slot>
      </div>
    `;
  }
}
